import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import { statsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { QrCode, Scan, Users, TrendUp } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { getStatusColor } from '../lib/utils';

export default function QRStatsPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const response = await statsApi.qr();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const summaryCards = [
    { title: t('qrStats.totalQrCodes'), value: stats?.summary?.total || 0, icon: QrCode, color: 'text-brand-600' },
    { title: t('qrStats.totalScans'), value: stats?.summary?.total_scans || 0, icon: Scan, color: 'text-brand-500' },
    { title: t('qrStats.internalScans'), value: stats?.summary?.internal_scans || 0, icon: Users, color: 'text-green-500' },
    { title: t('qrStats.consumerScans'), value: stats?.summary?.consumer_scans || 0, icon: TrendUp, color: 'text-purple-500' },
  ];

  const statusData = stats?.status_breakdown
    ? Object.entries(stats.status_breakdown).map(([name, value]) => ({ name, value }))
    : [];

  const dailyData = stats?.daily_scans || [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="qr-stats-page">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-heading font-semibold text-zinc-900">{t('qrStats.title')}</h1>
          <p className="text-zinc-500 mt-1">{t('qrStats.subtitle')}</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {summaryCards.map((stat, index) => (
            <Card key={index} className="bg-white border-zinc-200 shadow-sm" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs tracking-[0.1em] uppercase text-zinc-400 font-medium">{stat.title}</p>
                    <p className="text-2xl font-heading font-semibold text-zinc-900 mt-1">
                      {loading ? '-' : stat.value.toLocaleString()}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg bg-zinc-50 ${stat.color}`}>
                    <stat.icon weight="fill" className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts */}
        <Tabs defaultValue="trends" className="space-y-4">
          <TabsList className="bg-zinc-100 border border-zinc-200">
            <TabsTrigger value="trends" className="data-[state=active]:bg-brand-600 data-[state=active]:text-white">
              {t('qrStats.scanTrends')}
            </TabsTrigger>
            <TabsTrigger value="status" className="data-[state=active]:bg-brand-600 data-[state=active]:text-white">
              {t('qrStats.statusDistribution')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="trends">
            <Card className="bg-white border-zinc-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-zinc-900">Daily Scan Trends (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-[300px] flex items-center justify-center text-zinc-400">Loading...</div>
                ) : dailyData.length === 0 ? (
                  <div className="h-[300px] flex items-center justify-center text-zinc-400">
                    {t('qrStats.noScanData')}
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        stroke="#9ca3af"
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                        tickFormatter={(value) => value.split('-').slice(1).join('/')}
                      />
                      <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                        }}
                        labelStyle={{ color: '#18181B' }}
                        itemStyle={{ color: '#FA8904' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#FA8904"
                        strokeWidth={2}
                        dot={{ fill: '#FA8904', strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: '#FA8904' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="status">
            <Card className="bg-white border-zinc-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-zinc-900">{t('qrStats.statusDist')}</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-[300px] flex items-center justify-center text-zinc-400">Loading...</div>
                ) : statusData.length === 0 ? (
                  <div className="h-[300px] flex items-center justify-center text-zinc-400">
                    {t('qrStats.noQrCodes')}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={statusData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                        <YAxis dataKey="name" type="category" stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} width={100} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                          }}
                          labelStyle={{ color: '#18181B' }}
                          itemStyle={{ color: '#FA8904' }}
                        />
                        <Bar dataKey="value" fill="#FA8904" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="space-y-3">
                      {statusData.map((item) => (
                        <div key={item.name} className="flex items-center justify-between p-3 bg-zinc-50 rounded-md border border-zinc-200">
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(item.name)}`}>
                            {item.name}
                          </span>
                          <span className="text-zinc-900 font-medium">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
